<template>
  <div class="announcement-list-container">
    <!-- 现代化页面头部 -->
    <div class="page-header">
      <div class="header-content">
        <div class="header-left">
          <div class="header-info">
            <h1 class="page-title">
              <i class="el-icon-bell"></i>
              公告管理
            </h1>
            <p class="page-subtitle">管理系统公告、活动通知和重要消息发布</p>
          </div>
        </div>
        <div class="header-actions">
          <el-button type="primary" icon="el-icon-plus" @click="handleCreate" class="action-btn">
            新增公告
          </el-button>
        </div>
      </div>
    </div>

    <!-- 主要内容区域 -->
    <div class="main-content">
      <!-- 搜索筛选区域 -->
      <div class="search-section">
        <el-card class="search-card">
          <el-form :model="queryForm" label-width="80px" class="search-form">
            <el-row :gutter="20">
              <el-col :span="6">
                <el-form-item label="公告标题">
                  <el-input 
                    v-model="queryForm.keyword" 
                    placeholder="请输入公告标题" 
                    clearable 
                    size="medium"
                    @keyup.enter.native="handleSearch"
                  />
                </el-form-item>
              </el-col>
              <el-col :span="4">
                <el-form-item label="公告类型">
                  <el-select v-model="queryForm.type" placeholder="请选择类型" clearable size="medium" style="width: 100%">
                    <el-option label="系统公告" :value="1" />
                    <el-option label="活动公告" :value="2" />
                    <el-option label="重要通知" :value="3" />
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="4">
                <el-form-item label="状态">
                  <el-select v-model="queryForm.is_active" placeholder="请选择状态" clearable size="medium" style="width: 100%">
                    <el-option label="已发布" :value="true">
                      <span style="float: left">已发布</span>
                      <span style="float: right; color: #67C23A; font-weight: 600">
                        <i class="el-icon-success"></i> 已发布
                      </span>
                    </el-option>
                    <el-option label="已禁用" :value="false">
                      <span style="float: left">已禁用</span>
                      <span style="float: right; color: #F56C6C; font-weight: 600">
                        <i class="el-icon-error"></i> 已禁用
                      </span>
                    </el-option>
                  </el-select>
                </el-form-item>
              </el-col>
              <el-col :span="10">
                <el-form-item>
                  <el-button type="primary" @click="handleSearch" icon="el-icon-search" size="medium">搜索</el-button>
                  <el-button @click="handleReset" icon="el-icon-refresh" size="medium">重置</el-button>
                  <el-button @click="handleRefresh" icon="el-icon-refresh" size="medium">刷新</el-button>
                </el-form-item>
              </el-col>
            </el-row>
          </el-form>
        </el-card>
      </div>

      <!-- 统计卡片 -->
      <div class="stats-section">
        <el-row :gutter="20">
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon total">
                <i class="el-icon-bell"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ announcementList.length }}</div>
                <div class="stat-label">公告总数</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon active">
                <i class="el-icon-success"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ announcementList.filter(a => a.is_active).length }}</div>
                <div class="stat-label">已发布</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon popup">
                <i class="el-icon-message-solid"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ announcementList.filter(a => a.is_popup).length }}</div>
                <div class="stat-label">弹窗公告</div>
              </div>
            </div>
          </el-col>
          <el-col :span="6">
            <div class="stat-card">
              <div class="stat-icon expired">
                <i class="el-icon-time"></i>
              </div>
              <div class="stat-content">
                <div class="stat-number">{{ getExpiredCount() }}</div>
                <div class="stat-label">已过期</div>
              </div>
            </div>
          </el-col>
        </el-row>
      </div>

      <!-- 公告列表 -->
      <div class="table-section">
        <el-card class="table-card">
          <div slot="header" class="table-header">
            <span class="table-title">
              <i class="el-icon-tickets"></i> 公告列表
            </span>
            <span class="table-subtitle">共 {{ total }} 条记录</span>
          </div>

          <!-- 空状态提示 -->
          <div v-if="!tableLoading && announcementList.length === 0" class="empty-state">
            <div class="empty-icon">
              <i class="el-icon-bell"></i>
            </div>
            <div class="empty-text">
              <h3>暂无公告数据</h3>
              <p>点击"新增公告"按钮创建第一个公告</p>
            </div>
            <el-button type="primary" @click="handleCreate" class="empty-action">
              <i class="el-icon-plus"></i> 新增公告
            </el-button>
          </div>

          <el-table 
            v-else
            :data="announcementList" 
            v-loading="tableLoading" 
            stripe 
            style="width: 100%"
            @selection-change="handleSelectionChange"
            :header-cell-style="{ background: '#f8f9fa' }"
          >
            <el-table-column type="selection" width="55" />
            
            <el-table-column label="公告信息" min-width="400">
              <template slot-scope="scope">
                <div class="announcement-info">
                  <div class="announcement-header">
                    <div class="announcement-title">{{ scope.row.title }}</div>
                    <div class="announcement-subtitle" v-if="scope.row.subtitle">
                      {{ scope.row.subtitle }}
                    </div>
                    <div class="announcement-meta">
                      <el-tag v-if="scope.row.type === 1" size="mini" type="info">系统公告</el-tag>
                      <el-tag v-else-if="scope.row.type === 2" size="mini" type="primary">活动公告</el-tag>
                      <el-tag v-else-if="scope.row.type === 3" size="mini" type="warning">重要通知</el-tag>
                      <el-tag v-if="scope.row.is_popup" size="mini" type="success">弹窗</el-tag>
                      <span class="priority-tag" :class="getPriorityClass(scope.row.priority)">
                        {{ getPriorityText(scope.row.priority) }}
                      </span>
                    </div>
                  </div>
                  <div class="announcement-content">
                    <div class="content-summary" v-if="scope.row.summary">
                      {{ scope.row.summary }}
                    </div>
                    <div v-else class="content-preview">
                      {{ getContentPreview(scope.row.content) }}
                    </div>
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="统计信息" width="140" align="center">
              <template slot-scope="scope">
                <div class="stats-info">
                  <div class="stat-row">
                    <span class="stat-label">查看:</span>
                    <span class="stat-value">{{ scope.row.view_count || 0 }}</span>
                  </div>
                  <div class="stat-row">
                    <span class="stat-label">阅读:</span>
                    <span class="stat-value">{{ scope.row.read_count || 0 }}</span>
                  </div>
                  <el-button 
                    type="text" 
                    size="mini" 
                    icon="el-icon-data-line"
                    @click="handleViewStats(scope.row)"
                    class="stats-btn"
                  >
                    详情
                  </el-button>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="状态" width="100" align="center">
              <template slot-scope="scope">
                <el-tag :type="scope.row.is_active ? 'success' : 'danger'" size="medium">
                  {{ scope.row.is_active ? '已发布' : '已禁用' }}
                </el-tag>
              </template>
            </el-table-column>

            <el-table-column label="时间信息" width="160">
              <template slot-scope="scope">
                <div class="time-info">
                  <div class="time-item">
                    <i class="el-icon-time"></i>
                    发布：{{ formatDate(scope.row.publish_time, 'short') }}
                  </div>
                  <div v-if="scope.row.expire_time" class="time-item">
                    <i class="el-icon-timer"></i>
                    过期：{{ formatDate(scope.row.expire_time, 'short') }}
                  </div>
                </div>
              </template>
            </el-table-column>

            <el-table-column label="创建时间" width="160">
              <template slot-scope="scope">
                <div class="create-time">
                  <i class="el-icon-date"></i>
                  {{ formatDate(scope.row.created_at) }}
                </div>
              </template>
            </el-table-column>

            <el-table-column label="操作" width="300" fixed="right" align="center">
              <template slot-scope="scope">
                <div class="action-buttons">
                  <el-button 
                    size="mini" 
                    type="primary" 
                    icon="el-icon-edit" 
                    @click="handleEdit(scope.row)"
                  >
                    编辑
                  </el-button>
                  <el-button 
                    size="mini" 
                    :type="scope.row.is_active ? 'warning' : 'success'" 
                    :icon="scope.row.is_active ? 'el-icon-close' : 'el-icon-check'"
                    @click="handleToggleStatus(scope.row)"
                  >
                    {{ scope.row.is_active ? '禁用' : '启用' }}
                  </el-button>
                  <el-button 
                    size="mini" 
                    type="danger" 
                    icon="el-icon-delete" 
                    @click="handleDelete(scope.row)"
                  >
                    删除
                  </el-button>
                </div>
              </template>
            </el-table-column>
          </el-table>

          <!-- 分页 -->
          <div class="pagination-container">
            <el-pagination
              @size-change="handleSizeChange"
              @current-change="handleCurrentChange"
              :current-page="queryForm.page"
              :page-sizes="[10, 20, 50, 100]"
              :page-size="queryForm.page_size"
              :total="total"
              layout="total, sizes, prev, pager, next, jumper"
              background
            />
          </div>
        </el-card>
      </div>
    </div>

    <!-- 统计详情对话框 -->
    <el-dialog 
      title="公告统计详情" 
      :visible.sync="statsDialogVisible" 
      width="600px"
      :close-on-click-modal="false"
    >
      <div v-if="currentStats" class="stats-detail">
        <div class="stats-header">
          <h3>{{ currentStats.title }}</h3>
          <div class="stats-summary">
            <div class="summary-item">
              <span class="label">查看次数：</span>
              <span class="value">{{ currentStats.view_count || 0 }}</span>
            </div>
            <div class="summary-item">
              <span class="label">阅读次数：</span>
              <span class="value">{{ currentStats.read_count || 0 }}</span>
            </div>
            <div class="summary-item">
              <span class="label">阅读率：</span>
              <span class="value">{{ currentStats.read_rate || 0 }}%</span>
            </div>
          </div>
        </div>
        
        <div class="stats-charts" v-if="currentStats.recent_views">
          <h4>最近查看趋势</h4>
          <div class="chart-container">
            <div 
              v-for="item in currentStats.recent_views" 
              :key="item.date"
              class="chart-item"
            >
              <div class="date">{{ item.date }}</div>
              <div class="data-bars">
                <div class="bar-item">
                  <span class="bar-label">查看</span>
                  <div class="bar">
                    <div 
                      class="bar-fill view-bar" 
                      :style="{width: getBarWidth(item.views, 'view')}"
                    ></div>
                  </div>
                  <span class="bar-value">{{ item.views }}</span>
                </div>
                <div class="bar-item">
                  <span class="bar-label">阅读</span>
                  <div class="bar">
                    <div 
                      class="bar-fill read-bar" 
                      :style="{width: getBarWidth(item.reads, 'read')}"
                    ></div>
                  </div>
                  <span class="bar-value">{{ item.reads }}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div class="target-reach" v-if="currentStats.target_reach">
          <h4>触达统计</h4>
          <el-row :gutter="20">
            <el-col :span="8">
              <div class="reach-item">
                <div class="reach-number">{{ currentStats.target_reach.total_users }}</div>
                <div class="reach-label">目标用户</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="reach-item">
                <div class="reach-number">{{ currentStats.target_reach.viewed_users }}</div>
                <div class="reach-label">已查看</div>
              </div>
            </el-col>
            <el-col :span="8">
              <div class="reach-item">
                <div class="reach-number">{{ currentStats.target_reach.read_users }}</div>
                <div class="reach-label">已阅读</div>
              </div>
            </el-col>
          </el-row>
        </div>
      </div>
      
      <div slot="footer">
        <el-button @click="statsDialogVisible = false">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { getAnnouncements, deleteAnnouncement, updateAnnouncementStatus, getAnnouncementStatistics } from '@/api/announcement'

export default {
  name: 'AnnouncementList',
  data() {
    return {
      queryForm: {
        page: 1,
        page_size: 20,
        keyword: '',
        type: null,
        is_active: null
      },
      
      announcementList: [],
      total: 0,
      tableLoading: false,
      selectedAnnouncements: [],
      
      // 统计对话框相关
      statsDialogVisible: false,
      currentStats: null,
      statsLoading: false
    }
  },
  
  created() {
    this.fetchData()
  },
  
  methods: {
    async fetchData() {
      this.tableLoading = true
      try {
        const response = await getAnnouncements(this.queryForm)
        this.announcementList = response.data.list || []
        this.total = response.data.pagination?.total || 0
      } catch (error) {
        console.error('获取公告列表失败:', error)
        this.$message.error('获取公告列表失败')
      } finally {
        this.tableLoading = false
      }
    },
    
    handleSearch() {
      this.queryForm.page = 1
      this.fetchData()
    },
    
    handleReset() {
      this.queryForm = {
        page: 1,
        page_size: 20,
        keyword: '',
        type: null,
        is_active: null
      }
      this.fetchData()
    },
    
    handleRefresh() {
      this.fetchData()
    },
    
    handleCreate() {
      this.$router.push('/admin/announcements/create')
    },
    
    handleEdit(announcement) {
      this.$router.push(`/admin/announcements/${announcement.id}/edit`)
    },
    
    async handleToggleStatus(announcement) {
      try {
        const newStatus = !announcement.is_active
        await updateAnnouncementStatus(announcement.id, newStatus)
        announcement.is_active = newStatus
        this.$message.success(`公告${newStatus ? '发布' : '禁用'}成功`)
      } catch (error) {
        this.$message.error('状态更新失败')
      }
    },
    
    handleDelete(announcement) {
      this.$confirm('确定要删除这个公告吗？', '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning'
      }).then(async () => {
        try {
          await deleteAnnouncement(announcement.id)
          this.$message.success('删除成功')
          this.fetchData()
        } catch (error) {
          this.$message.error('删除失败')
        }
      })
    },
    
    handleSelectionChange(selection) {
      this.selectedAnnouncements = selection
    },
    
    handleSizeChange(size) {
      this.queryForm.page_size = size
      this.queryForm.page = 1
      this.fetchData()
    },
    
    handleCurrentChange(page) {
      this.queryForm.page = page
      this.fetchData()
    },
    
    formatDate(date, format = 'full') {
      if (!date) return ''
      const d = new Date(date)
      if (format === 'short') {
        return d.toLocaleDateString('zh-CN')
      }
      return d.toLocaleString('zh-CN')
    },
    
    getExpiredCount() {
      const now = new Date()
      return this.announcementList.filter(announcement => {
        return announcement.expire_time && new Date(announcement.expire_time) < now
      }).length
    },
    
    getPriorityText(priority) {
      const priorityMap = {
        1: '低',
        2: '中', 
        3: '高'
      }
      return priorityMap[priority] || '未知'
    },
    
    getPriorityClass(priority) {
      const classMap = {
        1: 'priority-low',
        2: 'priority-medium',
        3: 'priority-high'
      }
      return classMap[priority] || ''
    },
    
    // 获取内容预览
    getContentPreview(content) {
      if (!content) return ''
      // 移除HTML标签
      const plainText = content.replace(/<[^>]*>/g, '')
      return plainText.length > 100 ? plainText.substring(0, 100) + '...' : plainText
    },
    
    // 查看统计详情
    async handleViewStats(announcement) {
      this.statsLoading = true
      try {
        const response = await getAnnouncementStatistics(announcement.id)
        this.currentStats = response.data
        this.statsDialogVisible = true
      } catch (error) {
        console.error('获取统计信息失败:', error)
        this.$message.error('获取统计信息失败')
      } finally {
        this.statsLoading = false
      }
    },
    
    // 获取进度条宽度
    getBarWidth(value, type) {
      if (!this.currentStats || !this.currentStats.recent_views) return '0%'
      
      // 获取同类型的最大值
      const maxValue = Math.max(...this.currentStats.recent_views.map(item => 
        type === 'view' ? item.views : item.reads
      ))
      
      if (maxValue === 0) return '0%'
      return Math.round((value / maxValue) * 100) + '%'
    }
  }
}
</script>

<style scoped>
.announcement-list-container {
  min-height: 100vh;
  background: #f5f7fa;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* 复用轮播图列表的基础样式 */
.page-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 25px 40px;
  margin-bottom: 30px;
  box-shadow: 0 2px 10px rgba(102, 126, 234, 0.3);
}

.header-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1400px;
  margin: 0 auto;
}

.header-left {
  flex: 1;
}

.header-info .page-title {
  font-size: 28px;
  font-weight: 600;
  margin: 0 0 8px 0;
  display: flex;
  align-items: center;
  gap: 12px;
  color: white;
}

.header-info .page-title i {
  font-size: 32px;
  color: white;
}

.header-info .page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin: 0;
  line-height: 1.5;
  color: white;
}

.header-actions {
  display: flex;
  gap: 12px;
  align-items: center;
}

.action-btn {
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  backdrop-filter: blur(10px);
  transition: all 0.3s ease;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.2);
  border-color: rgba(255, 255, 255, 0.3);
  transform: translateY(-2px);
}

.main-content {
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 40px 40px;
}

/* 统计卡片样式 */
.stats-section {
  margin-bottom: 30px;
}

.stat-card {
  background: white;
  border-radius: 20px;
  padding: 24px 20px;
  display: flex;
  align-items: center;
  gap: 16px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid #f1f3f4;
}

.stat-card:hover {
  transform: translateY(-6px);
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.12);
}

.stat-icon {
  width: 60px;
  height: 60px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 26px;
  background: white;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
  position: relative;
}

.stat-icon::before {
  content: '';
  position: absolute;
  inset: -2px;
  border-radius: 22px;
  padding: 2px;
  background: linear-gradient(135deg, #4facfe, #00f2fe);
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  -webkit-mask-composite: xor;
}

.stat-icon i {
  color: #4facfe;
  z-index: 1;
}

.stat-icon.total::before {
  background: linear-gradient(135deg, #667eea, #764ba2);
}

.stat-icon.total i {
  color: #667eea;
}

.stat-icon.active::before {
  background: linear-gradient(135deg, #67c23a, #85ce61);
}

.stat-icon.active i {
  color: #67c23a;
}

.stat-icon.popup::before {
  background: linear-gradient(135deg, #409eff, #66b3ff);
}

.stat-icon.popup i {
  color: #409eff;
}

.stat-icon.expired::before {
  background: linear-gradient(135deg, #e6a23c, #eebe77);
}

.stat-icon.expired i {
  color: #e6a23c;
}

.stat-content {
  flex: 1;
}

.stat-number {
  font-size: 28px;
  font-weight: 700;
  color: #303133;
  line-height: 1;
  margin-bottom: 4px;
}

.stat-label {
  font-size: 14px;
  color: #666;
  font-weight: 500;
}

/* 搜索和表格样式 */
.search-section {
  margin-bottom: 30px;
}

.search-card,
.table-card {
  border: none;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  border-radius: 16px;
}

.table-card {
  overflow: hidden;
}

.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.table-title {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.table-subtitle {
  color: #666;
  font-size: 14px;
}

/* 公告信息样式 */
.announcement-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.announcement-header {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.announcement-title {
  font-weight: 600;
  color: #303133;
  font-size: 14px;
  line-height: 1.4;
}

.announcement-meta {
  display: flex;
  align-items: center;
  gap: 6px;
  flex-wrap: wrap;
}

.priority-tag {
  font-size: 11px;
  padding: 1px 6px;
  border-radius: 10px;
  color: white;
  font-weight: 500;
}

.priority-low {
  background: #909399;
}

.priority-medium {
  background: #e6a23c;
}

.priority-high {
  background: #f56c6c;
}

.announcement-content {
  color: #666;
  font-size: 12px;
  line-height: 1.4;
}

/* 阅读统计样式 */
.read-stats {
  text-align: center;
}

.read-count {
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
}

.read-label {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

/* 时间信息样式 */
.time-info {
  font-size: 12px;
}

.time-item {
  color: #666;
  margin-bottom: 2px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.create-time {
  color: #666;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
}

/* 操作按钮样式 */
.action-buttons {
  display: flex;
  gap: 10px;
  flex-wrap: nowrap;
  padding: 8px 6px;
  justify-content: center;
  align-items: center;
}

.action-buttons .el-button {
  min-width: 60px;
  font-size: 13px;
  padding: 7px 15px;
}

/* 分页样式 */
.pagination-container {
  display: flex;
  justify-content: center;
  margin-top: 30px;
  padding: 20px 0;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .main-content {
    padding: 0 20px 20px;
  }
  
  .page-header {
    padding: 16px 20px;
  }
  
  .header-content {
    flex-direction: column;
    align-items: flex-start;
    gap: 16px;
  }
  
  .stat-card {
    padding: 16px 12px;
  }
  
  .stat-number {
    font-size: 24px;
  }
  
  /* 移动端操作按钮调整 */
  .action-buttons {
    flex-direction: column;
    gap: 6px;
    padding: 6px 4px;
  }
  
  .action-buttons .el-button {
    min-width: 70px;
    font-size: 12px;
    padding: 6px 12px;
    width: 100%;
  }
  
  /* 移动端时间和统计信息调整 */
  .time-info {
    font-size: 11px;
  }
  
  .stats-info {
    font-size: 11px;
  }
  
  .stats-btn {
    font-size: 10px;
  }
}

/* 副标题样式 */
.announcement-subtitle {
  color: #909399;
  font-size: 13px;
  margin-top: 4px;
  line-height: 1.4;
}

/* 内容摘要样式 */
.content-summary {
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

.content-preview {
  color: #666;
  font-size: 13px;
  line-height: 1.5;
}

/* 统计信息样式 */
.stats-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  align-items: center;
}

.stat-row {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
}

.stat-label {
  color: #909399;
}

.stat-value {
  color: #303133;
  font-weight: 600;
}

.stats-btn {
  margin-top: 4px;
  font-size: 11px;
}

/* 统计对话框样式 */
.stats-detail {
  padding: 16px 0;
}

.stats-header h3 {
  margin: 0 0 16px 0;
  color: #303133;
}

.stats-summary {
  display: flex;
  gap: 24px;
  margin-bottom: 24px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.summary-item {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.summary-item .label {
  font-size: 12px;
  color: #909399;
  margin-bottom: 4px;
}

.summary-item .value {
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
}

.stats-charts h4 {
  margin: 0 0 16px 0;
  color: #303133;
}

.chart-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.chart-item {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
}

.chart-item .date {
  width: 80px;
  font-size: 12px;
  color: #666;
}

.data-bars {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.bar-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.bar-label {
  width: 30px;
  font-size: 11px;
  color: #909399;
}

.bar {
  flex: 1;
  height: 16px;
  background: #f5f5f5;
  border-radius: 8px;
  overflow: hidden;
}

.bar-fill {
  height: 100%;
  border-radius: 8px;
}

.view-bar {
  background: #409eff;
}

.read-bar {
  background: #67c23a;
}

.bar-value {
  width: 30px;
  font-size: 11px;
  color: #303133;
  text-align: right;
}

.target-reach {
  margin-top: 24px;
}

.target-reach h4 {
  margin: 0 0 16px 0;
  color: #303133;
}

.reach-item {
  text-align: center;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
}

.reach-number {
  font-size: 24px;
  font-weight: 600;
  color: #409eff;
  margin-bottom: 8px;
}

.reach-label {
  font-size: 14px;
  color: #666;
}

/* 空状态样式 */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 20px;
  text-align: center;
}

.empty-icon {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
}

.empty-icon i {
  font-size: 48px;
  color: #c0c4cc;
}

.empty-text h3 {
  font-size: 18px;
  color: #303133;
  margin: 0 0 8px 0;
  font-weight: 600;
}

.empty-text p {
  font-size: 14px;
  color: #909399;
  margin: 0 0 32px 0;
  line-height: 1.6;
}

.empty-action {
  font-size: 14px;
  padding: 12px 24px;
}

/* 超小屏幕适配 */
@media (max-width: 480px) {
  .action-buttons {
    flex-direction: row;
    flex-wrap: wrap;
    gap: 4px;
    padding: 4px 2px;
  }
  
  .action-buttons .el-button {
    font-size: 10px;
    padding: 4px 8px;
    min-width: 45px;
    flex: 1;
    max-width: 80px;
  }
  
  .action-buttons .el-button span {
    display: none;
  }
  
  .action-buttons .el-button i {
    margin: 0;
    font-size: 12px;
  }
  
  .announcement-info {
    padding: 8px 0;
  }
  
  .announcement-title {
    font-size: 13px;
  }
  
  .content-summary,
  .content-preview {
    font-size: 12px;
    line-height: 1.4;
  }
}
</style> 